import { Grid, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import FormField from "layouts/pages/users/new-user/components/FormField";
import { useEffect, useState } from "react";
import { Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { useUser } from "context/UserContext";
import MDSelect from "components/MDSelect";
import DevedorEmailSection from "./DevedorEmailSection";
import DevedorTelefoneSection from "./DevedorTelefoneSection";
import DevedorEnderecoSection from "./DevedorEnderecoSection";
import { get, post, postNoResponse, postUploadNoResponse } from "utils/WebService";
import MDSnackbar from "components/MDSnackbar";
import { successAlert } from "utils/Functions";

const validationSchema = Yup.object().shape({
    nomeDevedor: Yup.string().required("Campo obrigatório"),
    codigoCliente: Yup.string().required("Campo obrigatório"),
    idPessoa: Yup.string().required("Selecione um cliente"),
    emails: Yup.array().of(
        Yup.object().shape({
            ccEmail: Yup.string().test('is-valid-email', 'E-mail inválido', value => {
                if (!value) return true;
                const strEmail = String(value).toLowerCase();
                const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!re.test(strEmail)) return false;
                if (strEmail.endsWith("@gamil.com")) return false;
                if (strEmail.endsWith("@hotmai.com") || strEmail.endsWith("@hotamil.com") || strEmail.endsWith("@outlok.com")) return false;
                return true;
            })
        })
    )
});

export default function CadastroBasico({ formRef, recordId, isJuridico, closeCreateEdit, isViewOnly, pendingContratos }) {
    const { processoStatus, userPerfils } = useUser();
    const isAdminOrSuporte = userPerfils && userPerfils.some(p => p?.tipoPerfil === 4 || p?.tipoPerfil === 6 || p?.nome === 'Administrador' || p?.nome === 'Suporte' || p === 'Administrador' || p === 'Suporte');
    const [usuarios, setUsuarios] = useState([]);
    const [statusFicha, setStatusFicha] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [marcacoes, setMarcacoes] = useState([]);
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [openDevolverModal, setOpenDevolverModal] = useState(false);
    const [devolverObs, setDevolverObs] = useState("");
    const [devolverFile, setDevolverFile] = useState(null);
    const [isSubmittingDevolucao, setIsSubmittingDevolucao] = useState(false);
    const [confirmAction, setConfirmAction] = useState({ open: false, title: "", message: "", onConfirm: null });

    useEffect(() => {
        get("/pessoa/list-all").then(({ list }) => setClientes(list || [])).catch(() => {});

        get(`/users/list-all-operators`)
            .then((res) => {
                setUsuarios(res.list || res.data || (Array.isArray(res) ? res : []));
            }).catch(() => {
                setUsuarios([]);
            });

        get("/status-ficha/list-all").then((res) => {
            let list = res.list || res.data || (Array.isArray(res) ? res : []);
            setStatusFicha(list.sort((a, b) => String(a.ccNome || a.nome || "").localeCompare(String(b.ccNome || b.nome || ""))));
        }).catch(() => {
            setStatusFicha([]);
        });

        get("/marcacoes").then((res) => {
            const list = res.list || res.data || (Array.isArray(res) ? res : []);
            setMarcacoes(list.filter(m => m.cvAlvo === 1 || m.cvAlvo === 3));
        }).catch(() => setMarcacoes([]));
    }, []);

    return (
        <MDBox pb={3}>
            <Formik
                innerRef={formRef}
                initialValues={{
                    id: recordId || null,
                    idPessoa: null,
                    nomeDevedor: '',
                    cpfCnpj: '',
                    codigoCliente: '',
                    idUsuario: null,
                    status: null,
                    emails: [],
                    enderecos: [],
                    telefones: [],
                    marcacoesIds: [],
                    observacao: ''
                }}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting, resetForm }) => {
                    const payload = {
                        id: values.id,
                        idPessoa: values.idPessoa,
                        nome: values.nomeDevedor,
                        documento: values.cpfCnpj,
                        codigoCliente: values.codigoCliente,
                        idUsuario: values.idUsuario,
                        idStatusFicha: values.status,
                        emails: values.emails,
                        enderecos: values.enderecos,
                        telefones: values.telefones,
                        marcacoesIds: values.marcacoesIds,
                        contratos: pendingContratos || [],
                        observacao: values.observacao
                    };
                    
                    if (!values.id && (!pendingContratos || pendingContratos.length === 0)) {
                        alert("Não é possível salvar o devedor sem cadastrar um débito/contrato. Vá até a aba Contratos e adicione ao menos um.");
                        setSubmitting(false);
                        return;
                    }

                    setErrorSB(false);
                    setSuccessSB(false);

                    return postNoResponse("/devedor/save", payload)
                        .then(() => {
                            setSuccessSB(true);
                            setSubmitting(false);
                            resetForm({ values });
                            if (!values.id && closeCreateEdit) {
                                successAlert("Ficha do devedor salva com sucesso!", closeCreateEdit);
                            }
                        })
                        .catch((error) => {
                            console.error("Erro ao salvar devedor", error);
                            setErrorSB(true);
                            setSubmitting(false);
                        });
                }}
            >
                {({ values, initialValues, handleChange, setFieldValue, errors, touched, isSubmitting, dirty }) => {
                    const isEmailsDirty = JSON.stringify(values.emails) !== JSON.stringify(initialValues.emails);
                    const isTelefonesDirty = JSON.stringify(values.telefones) !== JSON.stringify(initialValues.telefones);
                    const isEnderecosDirty = JSON.stringify(values.enderecos) !== JSON.stringify(initialValues.enderecos);
                    return (
                    <Form>
                        {/* Dummy inputs to intercept Chrome autofill */}
                        <input type="text" name="dummy_username" style={{ display: 'none' }} aria-hidden="true" autoComplete="username" />
                        <input type="password" name="dummy_password" style={{ display: 'none' }} aria-hidden="true" autoComplete="current-password" />
                        <FetchAndSetData recordId={recordId} usuarios={usuarios} setUsuarios={setUsuarios} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Cliente</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nomeComum}
                                    name="idPessoa"
                                    placeholder="Selecione..."
                                    value={clientes.find(c => c.id === values.idPessoa) || null}
                                    options={clientes}
                                    onChange={(value) => {
                                        setFieldValue("idPessoa", value ? value.id : null);
                                    }}
                                    isDisabled={isViewOnly}
                                />
                                {errors.idPessoa && touched.idPessoa && (
                                    <MDTypography variant="caption" color="error">{errors.idPessoa}</MDTypography>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <FormField
                                    type="text"
                                    label="Nome"
                                    name="nomeDevedor"
                                    value={values.nomeDevedor}
                                    onChange={handleChange}
                                    onBlur={(e) => {
                                        if (e.target.value) {
                                            post("/devedor/list", { page: 0, pageSize: 1, ccNome: e.target.value }).then(res => {
                                                if (res.data && res.data.length > 0) {
                                                    if (res.data.some(d => d.id !== recordId)) alert("Já existe um devedor com este Nome na base!");
                                                }
                                            }).catch(() => {});
                                        }
                                    }}
                                    placeholder="João da Silva"
                                    error={errors.nomeDevedor && touched.nomeDevedor}
                                    disabled={isViewOnly}
                                    autoComplete="off"
                                />
                                {errors.nomeDevedor && touched.nomeDevedor && (
                                    <MDTypography variant="caption" color="error">{errors.nomeDevedor}</MDTypography>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <FormField
                                    type="text"
                                    label="CPF/CNPJ"
                                    name="cpfCnpj"
                                    value={values.cpfCnpj}
                                    onChange={handleChange}
                                    onBlur={(e) => {
                                        if (e.target.value) {
                                            post("/devedor/list", { page: 0, pageSize: 1, ccDocumento: e.target.value }).then(res => {
                                                if (res.data && res.data.length > 0) {
                                                    if (res.data.some(d => d.id !== recordId)) alert("Já existe um devedor com este CPF/CNPJ na base!");
                                                }
                                            }).catch(() => {});
                                        }
                                    }}
                                    placeholder="Informe CPF ou CNPJ"
                                    disabled={isViewOnly}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <FormField
                                    type="text"
                                    label="Cód. Cliente"
                                    name="codigoCliente"
                                    value={values.codigoCliente}
                                    onChange={handleChange}
                                    onBlur={(e) => {
                                        if (e.target.value) {
                                            post("/devedor/list", { page: 0, pageSize: 1, ccCodigoCliente: e.target.value }).then(res => {
                                                if (res.data && res.data.length > 0) {
                                                    if (res.data.some(d => d.id !== recordId)) alert("Já existe um devedor com este Código na base!");
                                                }
                                            }).catch(() => {});
                                        }
                                    }}
                                    placeholder="Cód. Único"
                                    error={errors.codigoCliente && touched.codigoCliente}
                                    disabled={isViewOnly}
                                />
                                {errors.codigoCliente && touched.codigoCliente && (
                                    <MDTypography variant="caption" color="error">{errors.codigoCliente}</MDTypography>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Propriedade</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nome}
                                    name="idUsuario"
                                    placeholder="Selecione..."
                                    value={usuarios.find(u => u.id === values.idUsuario) || null}
                                    options={usuarios}
                                    onChange={(value) => {
                                        setFieldValue("idUsuario", value ? value.id : null);
                                    }}
                                    isDisabled={isViewOnly}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Status</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.ccNome}
                                    name="status"
                                    placeholder="Selecione..."
                                    value={statusFicha.find(s => s.id === values.status) || null}
                                    options={statusFicha}
                                    onChange={(value) => {
                                        setFieldValue("status", value ? value.id : null);
                                        if (isViewOnly) {
                                            const payload = {
                                                id: values.id,
                                                idPessoa: values.idPessoa,
                                                nome: values.nome,
                                                documento: values.cpfCnpj,
                                                codigoCliente: values.codigoCliente,
                                                idUsuario: values.idUsuario,
                                                idStatusFicha: value ? value.id : null,
                                                emails: values.emails,
                                                enderecos: values.enderecos,
                                                telefones: values.telefones,
                                                marcacoesIds: values.marcacoesIds,
                                                contratos: pendingContratos || [],
                                                observacao: values.observacao
                                            };
                                            postNoResponse("/devedor/save", payload)
                                                .then(() => setSuccessSB(true))
                                                .catch(() => setErrorSB(true));
                                        }
                                    }}
                                    isDisabled={false}
                                />
                            </Grid>
                            
                            <Grid item xs={12} sm={4}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Marcação Especial</InputLabel>
                                <MDSelect
                                    isMulti={true}
                                    getOptionValue={(option) => option.cvId}
                                    getOptionLabel={(option) => option.ccNome}
                                    name="marcacoesIds"
                                    placeholder="Selecione as marcações..."
                                    value={marcacoes.filter(m => values.marcacoesIds && values.marcacoesIds.includes(m.cvId))}
                                    options={marcacoes}
                                    onChange={(selected) => {
                                        const val = selected ? selected.map(s => s.cvId) : [];
                                        setFieldValue("marcacoesIds", val);
                                    }}
                                    isDisabled={isViewOnly}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormField
                                    type="text"
                                    label="Observação"
                                    name="observacao"
                                    value={values.observacao}
                                    onChange={handleChange}
                                    placeholder="Digite suas observações..."
                                    multiline
                                    rows={3}
                                    InputProps={{
                                        readOnly: isViewOnly,
                                        sx: isViewOnly ? { backgroundColor: "#f0f2f5", cursor: "default" } : {}
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <DevedorEmailSection values={values} setFieldValue={setFieldValue} handleChange={handleChange} errors={errors} touched={touched} isViewOnly={isViewOnly} />
                                {isViewOnly && (
                                    <MDBox display="flex" justifyContent="flex-end" mt={1}>
                                        <MDButton variant="outlined" color="success" size="small" type="submit" disabled={!isEmailsDirty || isSubmitting}>
                                            Salvar E-mails
                                        </MDButton>
                                    </MDBox>
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <DevedorTelefoneSection values={values} setFieldValue={setFieldValue} handleChange={handleChange} isViewOnly={isViewOnly} />
                                {isViewOnly && (
                                    <MDBox display="flex" justifyContent="flex-end" mt={1}>
                                        <MDButton variant="outlined" color="success" size="small" type="submit" disabled={!isTelefonesDirty || isSubmitting}>
                                            Salvar Telefones
                                        </MDButton>
                                    </MDBox>
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <DevedorEnderecoSection values={values} setFieldValue={setFieldValue} handleChange={handleChange} isViewOnly={isViewOnly} />
                                {isViewOnly && (
                                    <MDBox display="flex" justifyContent="flex-end" mt={1}>
                                        <MDButton variant="outlined" color="success" size="small" type="submit" disabled={!isEnderecosDirty || isSubmitting}>
                                            Salvar Endereços
                                        </MDButton>
                                    </MDBox>
                                )}
                            </Grid>
                            
                            <Grid item xs={12} display="flex" justifyContent="flex-end" mt={2} gap={2}>
                                {recordId && (
                                    isAdminOrSuporte ? (
                                        <MDButton variant="gradient" color="error" onClick={() => {
                                            setConfirmAction({
                                                open: true,
                                                title: "Devolver Ficha",
                                                message: "Deseja realmente devolver esta ficha?",
                                                onConfirm: async () => {
                                                    try {
                                                        await postNoResponse(`/devedor/devolver/${recordId}`, {});
                                                        setSuccessSB(true);
                                                        successAlert("Ficha devolvida com sucesso!", closeCreateEdit);
                                                    } catch (e) {
                                                        setErrorSB(true);
                                                    }
                                                }
                                            });
                                        }} disabled={isSubmitting || values.status === 1}>
                                            Devolver Ficha
                                        </MDButton>
                                    ) : (
                                        <MDButton variant="gradient" color="warning" onClick={() => setOpenDevolverModal(true)} disabled={isSubmitting || values.status === 1}>
                                            Solicitar Devolução
                                        </MDButton>
                                    )
                                )}
                                {recordId && values.status === 1 && isAdminOrSuporte && (
                                    <MDButton variant="gradient" color="success" onClick={() => {
                                        setConfirmAction({
                                            open: true,
                                            title: "Reativar Ficha",
                                            message: "Deseja realmente reativar esta ficha?",
                                            onConfirm: async () => {
                                                try {
                                                    await postNoResponse(`/devedor/reativar/${recordId}`, {});
                                                    setSuccessSB(true);
                                                    successAlert("Ficha reativada com sucesso!", closeCreateEdit);
                                                } catch (e) {
                                                    setErrorSB(true);
                                                }
                                            }
                                        });
                                    }} disabled={isSubmitting}>
                                        Reativar Ficha
                                    </MDButton>
                                )}
                                {!isViewOnly && (
                                    <MDButton variant="gradient" color="info" type="submit" disabled={!dirty || isSubmitting}>
                                        Salvar Cadastro
                                    </MDButton>
                                )}
                            </Grid>
                        </Grid>
                    </Form>
                    );
                }}
            </Formik>
            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content="Ficha do devedor salva com sucesso!"
                dateTime=""
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content="Ocorreu um erro ao salvar o devedor."
                dateTime=""
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite
            />

            <Dialog open={openDevolverModal} onClose={() => !isSubmittingDevolucao && setOpenDevolverModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Solicitar Devolução de Ficha</DialogTitle>
                <DialogContent>
                    <Box mt={2}>
                        <TextField
                            label="Motivo / Observação"
                            multiline
                            rows={4}
                            fullWidth
                            value={devolverObs}
                            onChange={(e) => setDevolverObs(e.target.value)}
                            disabled={isSubmittingDevolucao}
                            required
                        />
                    </Box>
                    <Box mt={2}>
                        <MDTypography variant="caption" fontWeight="bold">Anexar Arquivo (Opcional)</MDTypography>
                        <input
                            type="file"
                            onChange={(e) => setDevolverFile(e.target.files[0])}
                            disabled={isSubmittingDevolucao}
                            style={{ display: "block", marginTop: "8px" }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDevolverModal(false)} disabled={isSubmittingDevolucao}>Cancelar</Button>
                    <Button 
                        onClick={async () => {
                            if (!devolverObs) {
                                alert("Preencha o motivo/observação.");
                                return;
                            }
                            setIsSubmittingDevolucao(true);
                            try {
                                await postNoResponse(`/devedor/solicitar-devolucao/${recordId}`, { observacao: devolverObs });
                                if (devolverFile) {
                                    const formData = new FormData();
                                    formData.append("file", devolverFile);
                                    await postUploadNoResponse(`/document/devedor/upload/${recordId}`, formData);
                                }
                                setOpenDevolverModal(false);
                                setSuccessSB(true);
                                setIsSubmittingDevolucao(false);
                                successAlert("Solicitação de devolução enviada com sucesso!", closeCreateEdit);
                            } catch (e) {
                                setErrorSB(true);
                                setIsSubmittingDevolucao(false);
                            }
                        }} 
                        variant="contained" 
                        color="primary" 
                        disabled={isSubmittingDevolucao}
                        sx={{ color: "#fff" }}
                    >
                        {isSubmittingDevolucao ? <CircularProgress size={24} color="inherit" /> : "Enviar Solicitação"}
                    </Button>
                </DialogActions>
            </Dialog>
        </MDBox>
    );
}

const FetchAndSetData = ({ recordId, usuarios, setUsuarios }) => {
    const { resetForm } = useFormikContext();

    useEffect(() => {
        if (recordId) {
            get(`/devedor/by-id/${recordId}`).then(data => {
                if (data.idUsuario && !usuarios.find(u => u.id === data.idUsuario)) {
                    setUsuarios(prev => {
                        if (!prev.find(u => u.id === data.idUsuario)) {
                            return [...prev, { id: data.idUsuario, nome: data.nomeUsuario || "Operador Sem Acesso / Inativo" }];
                        }
                        return prev;
                    });
                }
                resetForm({
                    values: {
                        id: data.id,
                        idPessoa: data.idPessoa,
                        nomeDevedor: data.nome || '',
                        cpfCnpj: data.documento || '',
                        codigoCliente: data.codigoCliente || '',
                        idUsuario: data.idUsuario,
                        status: data.idStatusFicha,
                        emails: data.emails || [],
                        enderecos: data.enderecos || [],
                        telefones: data.telefones || [],
                        marcacoesIds: data.marcacoesIds || [],
                        observacao: data.observacao || ''
                    }
                });
            }).catch(console.error);
        }
    }, [recordId, resetForm, usuarios, setUsuarios]);

    return null;
};
